import React, { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "../icons";

function childrenToLabel(node) {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(childrenToLabel).join("");
  if (React.isValidElement(node)) return childrenToLabel(node.props.children);
  return "";
}

function normalizeOptions(optionsProp, children) {
  if (Array.isArray(optionsProp) && optionsProp.length > 0) {
    return optionsProp.map((opt) => {
      if (opt != null && typeof opt === "object") {
        return {
          value: opt.value != null ? String(opt.value) : "",
          label:
            opt.label != null
              ? String(opt.label)
              : opt.value != null
                ? String(opt.value)
                : "",
          disabled: Boolean(opt.disabled),
        };
      }
      return {
        value: opt != null ? String(opt) : "",
        label: opt != null ? String(opt) : "",
        disabled: false,
      };
    });
  }

  const fromChildren = [];
  React.Children.forEach(children, (child) => {
    if (!child || child.type !== "option") return;
    const rawValue =
      child.props.value !== undefined ? child.props.value : child.props.children;
    const label = childrenToLabel(child.props.children);
    fromChildren.push({
      value: rawValue != null ? String(rawValue) : "",
      label: label || (rawValue != null ? String(rawValue) : ""),
      disabled: Boolean(child.props.disabled),
    });
  });
  return fromChildren;
}

/**
 * Styled single-select dropdown (gray focus + gray option hover).
 * Drop-in compatible with native <select> via name/value/onChange and <option> children.
 */
const CustomSelect = ({
  options: optionsProp,
  children,
  value = "",
  onChange,
  name = "",
  id,
  disabled = false,
  className = "",
  placeholder,
  required = false,
  searchable = false,
  searchPlaceholder = "Type to filter…",
  "aria-label": ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  const options = useMemo(
    () => normalizeOptions(optionsProp, children),
    [optionsProp, children],
  );

  const stringValue = value == null ? "" : String(value);
  const selectedOption = options.find((o) => o.value === stringValue);
  const displayLabel =
    selectedOption?.label ||
    (stringValue
      ? stringValue
      : placeholder || options.find((o) => o.value === "")?.label || "Select...");

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => {
      // Keep the empty/"All" option visible while filtering.
      if (option.value === "") return true;
      return (
        String(option.label || "")
          .toLowerCase()
          .includes(q) ||
        String(option.value || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [options, searchable, searchQuery]);

  const emitChange = (nextValue) => {
    if (!onChange) return;
    onChange({
      target: { name, value: nextValue },
      currentTarget: { name, value: nextValue },
    });
  };

  const updateMenuPosition = () => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const menuMax = 240;
    const openUp = spaceBelow < Math.min(menuMax, 160) && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      menuMax,
      openUp ? Math.max(120, spaceAbove - 8) : Math.max(120, spaceBelow - 8),
    );

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: Math.max(rect.width, 120),
      zIndex: 10000,
      maxHeight,
      ...(openUp
        ? { bottom: viewportH - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
  }, [isOpen, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      return undefined;
    }

    if (searchable) {
      const t = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        containerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleReposition = () => updateMenuPosition();
    const handleKey = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen]);

  const handleSelect = (option) => {
    if (option.disabled || disabled) return;
    emitChange(option.value);
    setSearchQuery("");
    setIsOpen(false);
  };

  const hasExplicitHeight = /\bh-\[|\bh-\d|\bmin-h-/.test(className);

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        onClick={() => {
          if (disabled) return;
          setIsOpen((open) => !open);
        }}
        data-open={isOpen ? "true" : "false"}
        className={`ims-dropdown-trigger w-full pr-9 pl-3 py-2 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px] text-left text-[var(--ink,#1c1c1a)] bg-white cursor-pointer flex items-center outline-none focus:outline-none focus-visible:outline-none disabled:bg-[var(--sunken,#eeece7)] disabled:text-[var(--muted,#6b6a66)] disabled:cursor-not-allowed ${
          hasExplicitHeight ? "" : "min-h-[38px]"
        } ${className}`}
      >
        <span
          className={`flex-1 truncate ${
            !selectedOption && !stringValue
              ? "text-[var(--faint,#9a9a95)]"
              : ""
          }`}
        >
          {displayLabel}
        </span>
        <ChevronDownIcon
          className={`absolute right-2.5 w-4 h-4 text-gray-500 pointer-events-none transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={menuStyle}
            className="ims-dropdown-panel bg-white border border-[var(--line,#e4e2dc)] rounded-md shadow-lg overflow-hidden flex flex-col"
          >
            {searchable ? (
              <div className="sticky top-0 z-10 border-b border-[var(--line,#e4e2dc)] bg-white p-2">
                <input
                  ref={searchInputRef}
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-md border border-[var(--line-strong,#d5d3cc)] px-2.5 py-1.5 text-[13px] text-[var(--ink,#1c1c1a)] outline-none focus:ring-1 focus:ring-[var(--accent,#a51c1c)]"
                  aria-label={searchPlaceholder}
                />
              </div>
            ) : null}
            <div className="py-1 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  {options.length === 0
                    ? "No options available"
                    : "No matching options"}
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = option.value === stringValue;
                  return (
                    <button
                      key={`${option.value}__${option.label}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={option.disabled}
                      onClick={() => handleSelect(option)}
                      className={`ims-dropdown-option w-full text-left px-3 py-2 text-sm transition-colors outline-none focus:outline-none break-words whitespace-normal ${
                        option.disabled
                          ? "text-gray-400 cursor-not-allowed"
                          : "cursor-pointer text-gray-700"
                      } ${isSelected ? "font-medium" : ""}`}
                    >
                      {option.label}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CustomSelect;
